import type { useAuthStore } from '../stores/authStore';

type AuthStore = ReturnType<typeof useAuthStore>;

/**
 * Install a global `fetch` interceptor that:
 *
 * 1. Adds `Authorization: Bearer <token>` to every `/api/*` request when
 *    the auth store has a token.
 * 2. On 401, clears the local token and triggers `authStore.login()` —
 *    a full-page redirect through /auth/login. The app's in-memory
 *    state is lost (deep-link restoration is out of scope for v1).
 *
 * Returns the original fetch so callers can restore it during tests.
 */
export function installFetchInterceptor(authStore: AuthStore): () => void {
    const originalFetch = window.fetch;
    const boundOriginal = originalFetch.bind(window);

    window.fetch = async (input, init) => {
        if (!isApiRequest(input)) {
            return boundOriginal(input, init);
        }

        const headers = new Headers(init?.headers);
        if (authStore.token) {
            headers.set('Authorization', `Bearer ${authStore.token}`);
        }

        const response = await boundOriginal(input, { ...init, headers });

        if (response.status === 401) {
            // Token gone/expired/invalid. Clear local state, stash the
            // current path so we can deep-link back after re-auth, and
            // bounce back through /auth/login (full-page redirect).
            authStore.clearToken();
            authStore.login(window.location.pathname + window.location.search);
        }

        return response;
    };

    return () => {
        window.fetch = originalFetch;
    };
}


function isApiRequest(input: RequestInfo | URL): boolean {
    const url = urlOf(input);
    return url.startsWith('/api/') || pathOf(url).startsWith('/api/');
}


function urlOf(input: RequestInfo | URL): string {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    return input.url;
}


function pathOf(url: string): string {
    try {
        return new URL(url).pathname;
    } catch {
        return url;
    }
}
